import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Share2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import blogArticles, { BLOG_IMAGES } from '@/data/blogArticles';
import SEOHead from '@/components/SEOHead';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const article = blogArticles.find(a => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center dark:bg-zinc-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2 dark:text-zinc-50">Artikel Tidak Ditemukan</h1>
          <Link to="/blog"><Button variant="outline">Kembali ke Blog</Button></Link>
        </div>
      </div>
    );
  }

  // Find related articles (same category, exclude current)
  const related = blogArticles.filter(a => a.category === article.category && a.id !== article.id).slice(0, 3);

  const shareUrl = `https://nerava.id/blog/${article.slug}`;
  const ogImage = BLOG_IMAGES[article.category] || 'https://nerava.id/og-image.png';

  // JSON-LD structured data for this article
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.excerpt,
    "image": ogImage,
    "datePublished": article.date,
    "author": { "@type": "Organization", "name": "Nerava" },
    "publisher": { "@type": "Organization", "name": "Nerava", "logo": { "@type": "ImageObject", "url": "https://nerava.id/favicon-192.png" } },
    "mainEntityOfPage": { "@type": "WebPage", "@id": shareUrl },
    "keywords": article.keywords
  };

  // Render markdown-like content
  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return <h2 key={i} className="text-xl sm:text-2xl font-bold text-slate-900 mt-8 mb-4 dark:text-zinc-50">{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={i} className="text-lg font-semibold text-slate-900 mt-6 mb-3 dark:text-zinc-50">{line.replace('### ', '')}</h3>;
      }
      if (line.startsWith('- **')) {
        const parts = line.replace('- **', '').split('**');
        return <li key={i} className="ml-4 text-slate-600 mb-1"><strong className="text-slate-800 dark:text-zinc-50">{parts[0]}</strong>{parts[1]}</li>;
      }
      if (line.startsWith('- ')) {
        return <li key={i} className="ml-4 text-slate-600 mb-1">{line.replace('- ', '')}</li>;
      }
      if (line.startsWith('| ') || line.startsWith('|')) {
        const cells = line.split('|').filter(c => c.trim());
        // Skip separator rows (|------|------|)
        if (cells.every(c => c.trim().match(/^[-:]+$/))) return null;
        // Detect if this is header row (first table row)
        const isHeader = i === 0 || !content.split('\n')[i - 1]?.startsWith('|');
        return (
          <div key={i} className={`grid gap-2 text-sm border-b border-slate-100 py-2 ${isHeader ? 'font-semibold text-slate-800 bg-slate-50 rounded px-2' : 'px-2'} dark:text-zinc-50 dark:bg-zinc-900`} style={{ gridTemplateColumns: `repeat(${cells.length}, 1fr)` }}>
            {cells.map((cell, j) => (
              <span key={j} className={isHeader ? 'text-slate-800' : j === 0 ? 'font-medium text-slate-700' : 'text-slate-600'}>{cell.trim()}</span>
            ))}
          </div>
        );
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-semibold text-slate-800 mt-4 mb-2 dark:text-zinc-50">{line.replace(/\*\*/g, '')}</p>;
      }
      if (line.trim() === '') return <div key={i} className="h-3" />;
      // Bold inline
      const formatted = line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      return <p key={i} className="text-slate-600 leading-relaxed mb-2" dangerouslySetInnerHTML={{ __html: formatted }} />;
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-zinc-900">
      <SEOHead
        title={`${article.title} — Nerava Blog`}
        description={article.excerpt}
        canonical={shareUrl}
        ogImage={ogImage}
        ogType="article"
        article={{ date: article.date, category: article.category, keywords: article.keywords }}
        jsonLd={articleSchema}
      />

      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 dark:bg-zinc-900">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/blog">
              <Button variant="ghost" size="sm"><ArrowLeft className="w-5 h-5" /></Button>
            </Link>
            <Link to="/" className="flex items-center gap-2">
              <img src="/images/logo.png" alt="Nerava" className="w-7 h-7" onError={e => e.target.style.display = 'none'} />
              <span className="font-bold text-slate-900 text-sm dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>Nerava Blog</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(article.title + ' ' + shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400"
              title="Share via WhatsApp"
            >
              <Share2 className="w-4 h-4" />
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Hero Image */}
        {BLOG_IMAGES[article.category] && (
          <div className="rounded-xl overflow-hidden mb-8 h-48 sm:h-64">
            <img src={BLOG_IMAGES[article.category]} alt={article.title} className="w-full h-full object-cover" />
          </div>
        )}

        {/* Meta */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 font-medium dark:text-indigo-400 dark:bg-indigo-950/40">{article.category}</span>
            <span className="text-xs text-slate-500 flex items-center gap-1 dark:text-zinc-400"><Clock className="w-3 h-3" />{article.readTime}</span>
            <span className="text-xs text-slate-500 dark:text-zinc-400">{new Date(article.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>
            {article.title}
          </h1>
          <p className="text-lg text-slate-500 mt-3 dark:text-zinc-400">{article.excerpt}</p>
        </div>

        {/* Content */}
        <article className="bg-white rounded-xl border border-slate-200 p-6 sm:p-10 dark:bg-zinc-900">
          {renderContent(article.content)}
        </article>

        {/* CTA */}
        <div className="mt-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 sm:p-8 text-white">
          <h3 className="text-xl font-bold mb-2">Coba Nerava Gratis 30 Hari</h3>
          <p className="text-blue-100 text-sm mb-4">AI customer service untuk WhatsApp, Instagram, Messenger, dan TikTok Shop. Tanpa kartu kredit.</p>
          <Link to="/register">
            <Button className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold dark:bg-zinc-900 dark:text-indigo-400">Daftar Sekarang</Button>
          </Link>
        </div>

        {/* Related Articles */}
        {related.length > 0 && (
          <div className="mt-12">
            <h3 className="text-lg font-bold text-slate-900 mb-4 dark:text-zinc-50">Artikel Terkait</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map(r => (
                <Link
                  key={r.id}
                  to={`/blog/${r.slug}`}
                  className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow dark:bg-zinc-900"
                >
                  <h4 className="font-medium text-slate-900 text-sm line-clamp-2 mb-2 dark:text-zinc-50">{r.title}</h4>
                  <span className="text-xs text-indigo-600 flex items-center gap-1 dark:text-indigo-400">Baca <ChevronRight className="w-3 h-3" /></span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Share */}
        <div className="mt-8 flex items-center justify-center gap-3">
          <span className="text-sm text-slate-500 dark:text-zinc-400">Bagikan:</span>
          <a href={`https://wa.me/?text=${encodeURIComponent(article.title + ' ' + shareUrl)}`} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-full bg-green-500 text-white text-sm font-medium hover:bg-green-600">WhatsApp</a>
          <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-full bg-slate-800 text-white text-sm font-medium hover:bg-slate-900">Twitter</a>
        </div>

        <div className="mt-12 text-center">
          <p className="text-slate-500 text-sm dark:text-zinc-400">© 2026 Nerava.id. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
};

export default BlogDetailPage;
